import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useServerUiScope } from '@features/server-ui/components/ServerUiDomainProvider';
import { useGetServerUiComponentQuery } from '@features/server-ui/store';
import type { ItemListData, ServerUiComponent } from '@features/server-ui/types';
import { useSubjectLanguageVariants } from '@features/user/store/hooks';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Alert, Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useRecordMedicationHistoryConsentMutation } from '../store/api';

import { MedicationHistoryRow } from './MedicationHistoryRow';

const HISTORY_LABEL = 'Medication history';

// Copy is parameterized on the org subject language ("patient"/"client") —
// hosts pass useSubjectLanguageVariants().singular (a web-app MUST).
const consentNeededCopy = (subject: string) =>
  `With recorded consent, we can pull this ${subject}’s pharmacy fill history from SureScripts so you can see what other prescribers have written.`;

const PULLING_COPY = 'Pulling fills from SureScripts… this usually takes a few seconds.';

export const NeedsConsentState: React.FC<{
  onConsentClick: () => void;
  isSubmitting: boolean;
  subject: string;
}> = ({ onConsentClick, isSubmitting, subject }) => (
  <Box sx={{ py: 3, textAlign: 'center' }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 480, mx: 'auto' }}>
      {consentNeededCopy(subject)}
    </Typography>
    <Button
      variant="contained"
      color="primary"
      size="small"
      onClick={onConsentClick}
      disabled={isSubmitting}
      sx={{ textTransform: 'none' }}
    >
      Log {subject} consent to pull history
    </Button>
  </Box>
);

/**
 * `consent_stale`: we hold a consent record but DoseSpot rejected the last
 * pull, so whatever renders below is not a complete history (GDN-3281). The
 * only recovery path is re-recording consent, which clears the marker and
 * re-queues the pull — same mutation as the first-time CTA, different copy.
 */
export const StaleConsentState: React.FC<{
  onReconfirmClick: () => void;
  isSubmitting: boolean;
  subject: string;
}> = ({ onReconfirmClick, isSubmitting, subject }) => (
  <Alert
    severity="warning"
    sx={{ my: 1, alignItems: 'center' }}
    action={
      <Button
        color="inherit"
        size="small"
        onClick={onReconfirmClick}
        disabled={isSubmitting}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
      >
        Re-confirm consent
      </Button>
    }
  >
    Medication history couldn’t be refreshed — re-confirm {subject} consent to
    resume pulling from SureScripts.
  </Alert>
);

const PULLING_ROW_COUNT = 4;

const SkeletonRow: React.FC = () => (
  <Box
    sx={{
      alignItems: 'center',
      borderBottom: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      gap: 1,
      py: 1,
      '&:last-child': { borderBottom: 'none' },
    }}
  >
    <Skeleton variant="circular" sx={{ flexShrink: 0, height: 8, width: 8 }} />
    <Skeleton variant="text" sx={{ flex: 1, fontSize: '0.875rem' }} />
    <Skeleton variant="text" sx={{ flexShrink: 0, fontSize: '0.75rem', width: 48 }} />
  </Box>
);

const emptyGrantedCopy = (subject: string) =>
  `No fills found for this ${subject} yet — once SureScripts returns data it'll appear here.`;

/**
 * Granted state: paginated list of fill events using the rich row layout.
 *
 * Mirrors HistoryItemList's "page-1-from-component.data, page-N via direct
 * component fetch" pattern, but renders MedicationHistoryRow per item so we
 * can show sig + supply + source label / DEA badge — the richer info the
 * shared HistoryItemRow doesn't surface.
 */
const GrantedState: React.FC<{ component: ServerUiComponent; subject: string }> = ({
  component,
  subject,
}) => {
  const [page, setPage] = useState(1);
  const { containerKey, entityId } = useServerUiScope();

  const { data: paginatedData, isFetching } = useGetServerUiComponentQuery(
    {
      containerKey: containerKey ?? '',
      typeKey: component.type_key,
      entityId: entityId ?? undefined,
      page,
    },
    { skip: page === 1 || !containerKey },
  );

  const baseData = component.data as ItemListData | undefined;
  const activeData = page === 1 ? baseData : (paginatedData?.data as ItemListData | undefined);
  const items = activeData?.items ?? [];
  const total = activeData?.total ?? baseData?.total ?? 0;
  const perPage = activeData?.per_page ?? baseData?.per_page ?? total;
  const totalPages = perPage > 0 ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const showPagination = total > perPage;

  if (isFetching) {
    return (
      <Box>
        {Array.from({ length: perPage || 10 }, (_, i) => (
          <SkeletonRow key={i} />
        ))}
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {emptyGrantedCopy(subject)}
      </Typography>
    );
  }

  return (
    <Box>
      {items.map((item, index) => (
        // ItemListItem has no id; (label, date) is unique-enough for
        // pharmacy fills and keeps rows stable across page transitions.
        <MedicationHistoryRow key={`${item.label}|${item.date ?? ''}|${index}`} item={item} />
      ))}
      {showPagination && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'center', mt: 1 }}
        >
          <Button
            size="small"
            disabled={page === 1 || isFetching}
            onClick={() => setPage(p => p - 1)}
            startIcon={<ChevronLeftIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', minWidth: 'auto', color: 'text.secondary' }}
          >
            Previous
          </Button>
          <Typography variant="caption" color="text.secondary">
            {page} of {totalPages}
          </Typography>
          <Button
            size="small"
            disabled={page >= totalPages || isFetching}
            onClick={() => setPage(p => p + 1)}
            endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', minWidth: 'auto', color: 'text.secondary' }}
          >
            Next
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export const PullingState: React.FC = () => (
  <Box>
    {Array.from({ length: PULLING_ROW_COUNT }, (_, i) => (
      <SkeletonRow key={i} />
    ))}
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', textAlign: 'center', mt: 1 }}
    >
      {PULLING_COPY}
    </Typography>
  </Box>
);

/**
 * Heading-less consent-aware list. Use this when the parent already
 * provides its own surrounding card / title (e.g. ClinicalChartRenderer
 * embeds this beneath its allergies sub-section). The exported
 * MedicationHistoryList wraps this with its own subtitle for standalone use.
 */
export const MedicationHistoryBody: React.FC<{ component: ServerUiComponent }> = ({
  component,
}) => {
  const data = component.data as ItemListData | undefined;
  // Default to `needed` so an unexpected server shape prompts the provider
  // to act rather than silently showing an empty granted list.
  const consentState = data?.consent?.state ?? 'needed';
  // DoseSpot rejected the last pull despite our consent record — the list
  // below (empty or not) is not a complete history (GDN-3281).
  const consentStale = data?.consent?.consent_stale ?? false;
  const { entityId } = useServerUiScope();
  const patientId = entityId ?? '';
  const { enqueueSnackbar } = useSnackbar();
  const { singular: subject } = useSubjectLanguageVariants();

  const [recordConsent, { isLoading: isSubmitting }] =
    useRecordMedicationHistoryConsentMutation();
  // Synchronous in-flight guard: `isSubmitting` only updates on re-render, so
  // two activations in the same render window would both read it as false
  // (house pattern: refs, not state, for async race guards).
  const consentSubmitRef = useRef(false);
  const { medicationHistory } = useEventTracking();

  useEffect(() => {
    if (!patientId) return;
    medicationHistory.opened({
      patient_id: patientId,
      consent_state: consentState,
      consent_stale: consentStale,
    });
    // Re-fire when consent state changes — each transition is an impression
    // for that state.
  }, [patientId, consentState, consentStale, medicationHistory]);

  const handleConsentClick = useCallback(async () => {
    // Guard beyond the button's rendered disabled state: a second activation
    // before the re-render would fire the mutation twice and double-count
    // consent_clicked (the recovery metric).
    if (!patientId || consentSubmitRef.current) return;
    consentSubmitRef.current = true;
    medicationHistory.consentClicked({ patient_id: patientId, reconfirm: consentStale });
    try {
      await recordConsent({ patientId }).unwrap();
      medicationHistory.consentSubmitted({ patient_id: patientId });
      // The componentRefreshMiddleware refetches medication_history when this
      // mutation fulfills — no manual refetch needed here.
    } catch {
      enqueueSnackbar(
        `Could not record consent. Confirm the ${subject} is connected to e-prescribing and try again.`,
        { variant: 'error' },
      );
    } finally {
      consentSubmitRef.current = false;
    }
  }, [patientId, consentStale, recordConsent, enqueueSnackbar, medicationHistory, subject]);

  if (consentState === 'needed') {
    return (
      <NeedsConsentState
        onConsentClick={handleConsentClick}
        isSubmitting={isSubmitting}
        subject={subject}
      />
    );
  }
  if (consentState === 'pulling') {
    return <PullingState />;
  }
  // Granted-only, matching the unified card: the backend never pairs stale
  // with other states, and an unexpected `unavailable + stale` shape must not
  // offer a re-confirm CTA that would fail on a not-connected patient.
  if (consentState === 'granted' && consentStale) {
    // Stale + existing fills coexist: older pulls landed before DoseSpot
    // started rejecting the refresh. Show what we have under the warning, but
    // never the "no fills found" empty copy — absence here proves nothing.
    const hasItems = (data?.items?.length ?? 0) > 0;
    return (
      <>
        <StaleConsentState
          onReconfirmClick={handleConsentClick}
          isSubmitting={isSubmitting}
          subject={subject}
        />
        {hasItems && <GrantedState component={component} subject={subject} />}
      </>
    );
  }
  return <GrantedState component={component} subject={subject} />;
};

/**
 * Standalone wrapper — adds a "Medication history" subtitle above the body.
 * Used when the component is rendered as its own card (not currently
 * registered, kept for future flexibility if the section assignment is
 * restored).
 */
export const MedicationHistoryList: React.FC<{ component: ServerUiComponent }> = ({
  component,
}) => (
  <Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
      {HISTORY_LABEL}
    </Typography>
    <MedicationHistoryBody component={component} />
  </Box>
);
